import React, {useState, useContext, useEffect, useRef, useMemo} from 'react';
import {
    Box,
    Card,
    Button,
    TextField,
    Select,
    MenuItem,
    Typography,
    Divider,
    CircularProgress,
    useTheme,
    useMediaQuery
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import TagIcon from '@mui/icons-material/Tag';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import Order, {OrderType, orderTypesWithLabels} from "../../../models/Order";
import {StateContext} from "../../../components/context/StateProvider";
import SocketClient from "../../../socket/SocketClient";
import { ListOrder, ListOrderInfo, FieldLabel } from "../components";
import { useSalesOrder } from "../hooks";
import Utils from "../../../utils/Utils";
import {useLocation} from "react-router-dom";
import Toasts from "../../../components/notificacion/Toasts";
import errorSound from "../../../assets/audio/error.mp3";
import SalesService from "../../../services/sales.service";
import {debounce} from "@mui/material";
import { ALTURA_BOTON, BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";

const Info = () => {

    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const orderIdParam = query.get('albaran') || query.get('pedido');
    const orderTipo = query.get('tipo');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    const {state, updateState} = useContext(StateContext);
    const {getSales, checkOrderN2} = useSalesOrder()
    const orderRef = useRef('');
    const [loading, setLoading] = useState(false);
    const [orderId, setOrderId] = useState( (orderIdParam !== null ? orderIdParam : null ));
    const [orderType, setOrderType] = useState((orderTipo !== null ? orderTipo : OrderType.Albaran.valueOf() ));
    const [orderTypeText, setOrderTypeText] = useState((orderTipo !== null ? orderTipo : OrderType.Albaran.valueOf() ));
    const [listOrderInfo, setListOrderInfo] = useState([]);

    useEffect(() => {

        if (orderId !== null) {
            setOrderId(orderId);
            setLoading(true);
            getSales(orderId, 1, orderTipo).then(() => {
                setLoading(false);
            });
        }

        const interval = setInterval(() => checkOrderN2(null), 120000);
        const socketClient = new SocketClient();
        socketClient.connect();

        socketClient.receiveUpdateSale(async (data) => {
            // Con deep-link (?albaran=...) el formulario no se renderiza y orderRef no
            // está atado a ningún input: usar el parámetro de la URL como fallback
            const numeroActual = orderRef.current?.value || orderIdParam;
            if (numeroActual && data[0].pedido === numeroActual) {
                await getSales(numeroActual, 1);
            }
        });

        return () => {
            clearInterval(interval);
            updateState({ toasts: [] });
            updateState({ products: [] });
            updateState({ order: new Order() });
            socketClient.disconnect();
        };
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();

        const orderValue = orderRef.current.value;

        if (orderValue !== "") {
            const isOrderValid = Utils.checkOrder(orderValue);
            setLoading(true);

            if (isOrderValid) {
                updateState({ products: [] });
                setListOrderInfo([]);

                let ordenObtenida;

                if (orderType !== OrderType.Factura.valueOf()) {
                    ordenObtenida = await getSales(orderValue, 1, orderType);
                }

                if (orderType !== OrderType.Albaran.valueOf()) {
                    const listOrderInfo = await SalesService.getSales(orderValue, orderType, state.year, false);
                    setListOrderInfo(listOrderInfo);

                    if ((ordenObtenida && !ordenObtenida.location) || !ordenObtenida) {
                        updateState({ products: [] });
                    }
                }
            } else {
                const toasts = Toasts.generateToast('Formato inválido: SERIE-ALBARAN', 'error');
                updateState({ toasts: [...state.toasts, toasts] });
                Utils.playAudio(errorSound);
            }

            setLoading(false);
        }
    };

    // Debounce memoizado (misma instancia entre renders): limpia espacios del número escaneado
    const handleOrderChange = useMemo(
        () => debounce((event) => {
            if (orderRef.current) {
                orderRef.current.value = event.target.value.replace(/\s/g, "");
            }
        }, 300),
        []
    );

    // Cancelar el debounce pendiente al desmontar
    useEffect(() => () => handleOrderChange.clear(), [handleOrderChange]);

    const handleChange = (event, type) => {
        if (type === 'orderType') {
            setOrderType(event.target.value);
            setOrderTypeText(event.target.value)
            setListOrderInfo([]);
            orderRef.current.value = '';
            updateState({ products: [] });
        }
    };

    const actualizarOrderTypeText = () => {
        setOrderTypeText(OrderType.Albaran.valueOf());
    }

    return (
        <Box id="sales" sx={{ width: '100%' }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            mb: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1
                        }}
                    >
                        <ManageSearchIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                        Info pedidos
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                        Consulta el detalle de un albarán, pedido o factura
                    </Typography>

                    {/* Buscador: se oculta cuando se llega por enlace directo con el número */}
                    {orderId === null && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2
                                }}
                            >
                            {/* Select de tipo de orden */}
                            <Box>
                                <FieldLabel icon={CategoryIcon}>Tipo</FieldLabel>
                                <Select
                                    id="list-order-type"
                                    name="list-order-type"
                                    value={orderType}
                                    fullWidth
                                    onChange={(e) => handleChange(e, 'orderType')}
                                    sx={{
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    {orderTypesWithLabels.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            {/* Input de número de orden */}
                            <Box>
                                <FieldLabel icon={TagIcon}>Número</FieldLabel>
                                <TextField
                                    type="text"
                                    id="order"
                                    name="order"
                                    fullWidth
                                    placeholder="Ej: 00-12345"
                                    inputRef={orderRef}
                                    onFocus={() => {
                                        orderRef.current.value = ''
                                    }}
                                    onChange={handleOrderChange}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff",
                                        },
                                    }}
                                />
                            </Box>

                            {/* Botón de búsqueda */}
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                sx={{ ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO }}
                            >
                                {loading ? 'Buscando...' : 'Buscar'}
                            </Button>
                            </Box>
                        </>
                    )}
                </Box>

                {/* Carga del enlace directo: aquí no hay botón donde mostrar el spinner */}
                {orderId !== null && loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size={40} sx={{ color: "#cc071e" }} />
                    </Box>
                )}

                {/* Contenido */}
                {!loading && (
                    <Box sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                        <ListOrderInfo listOrderInfo={listOrderInfo} onActualizarOrderTypeText={actualizarOrderTypeText}/>
                        <ListOrder nave={'1'} type={orderTypeText} handle={'ventas'} registerAction={SalesService.registerSale}
                                   order={orderRef} preparer={true} reviewer={true} camera={true} orderStateN2={false} autoAssignReviewer={false} />
                    </Box>
                )}
            </Card>
        </Box>
    );
};

export default Info;
